import axios from 'axios';
import AuthService from '../services/AuthService';

const isAuthUrl = (url = '') =>
  url.includes('/api/auth/') || url.includes('/oauth/token');

// Endpoints that should NOT trigger a token refresh on 401 — their failures
// are handled silently by the calling component.
const isSkipRefreshUrl = (url = '') =>
  url.includes('/credits');

/**
 * Register global axios interceptors.
 *
 * REQUEST — injects Authorization: Bearer <token> on every non-auth request.
 *
 * RESPONSE 401 — silent token refresh + retry.
 *   Deduplication lives in AuthService._refreshPromise: no matter how many
 *   concurrent requests hit 401 at the same time, only ONE HTTP call goes to
 *   /api/auth/refresh. Every caller awaits the same in-flight promise and
 *   retries with the resulting token.
 *
 *   original._retry prevents infinite loops: a retried request that gets
 *   another 401 is rejected immediately instead of triggering a second refresh.
 *
 * Returns a cleanup function — pass as useEffect return value to eject
 * interceptors on unmount and avoid duplicates on re-render.
 */
export const setupAxiosInterceptors = (onAuthFailure) => {
  const reqId = axios.interceptors.request.use((config) => {
    const token = AuthService.getAccessToken();
    if (token && !isAuthUrl(config.url)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const resId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;

      // Pass through: non-401, already-retried, auth-endpoint, or skip-refresh errors.
      if (
        !error.response ||
        error.response.status !== 401 ||
        original._retry ||
        isAuthUrl(original.url) ||
        isSkipRefreshUrl(original.url)
      ) {
        return Promise.reject(error);
      }

      // Mark before the async gap so a retry-triggered 401 doesn't loop.
      original._retry = true;

      try {
        // AuthService.refresh() is deduplicated via _refreshPromise:
        // concurrent callers share one HTTP call and all receive the new token.
        const newToken = await AuthService.refresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      } catch (refreshError) {
        // Refresh token expired or invalid — clear session and redirect.
        await AuthService.logout();
        onAuthFailure();
        return Promise.reject(refreshError);
      }
    }
  );

  return () => {
    axios.interceptors.request.eject(reqId);
    axios.interceptors.response.eject(resId);
  };
};
